import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeValesHooks } from './hooks';
import { EmployeeValeFormDialog } from './EmployeeValeFormDialog';
import { EmployeeValeParcelasDialog } from './EmployeeValeParcelasDialog';
import type { EmployeeValeDto, EmployeeValeListItem, ValeStatus } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataSolicitacao',
  sortOrder: 'desc',
};

export interface EmployeeValesManagerProps {
  employeeId: number;
}

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(status: ValeStatus): 'default' | 'success' | 'warning' {
  if (status === 'PAG') return 'success';
  if (status === 'CAN') return 'default';
  return 'warning'; // ABE
}

/**
 * CRUD reusável de Vales (T-23 Fase 1). PK do filho é `id` (autonum int).
 * Padrão espelha `EmployeeFaltasManager`/`EmployeeFeriasManager`, com
 * diferenças:
 *   - Update só permitido se `status=ABE` (botão Edit fica disabled em PAG/CAN).
 *   - Delete é soft via `status=CAN` (botão Cancelar — não Excluir — e só
 *     habilitado se `status=ABE`).
 */
export function EmployeeValesManager({ employeeId }: EmployeeValesManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('colaborador-vales', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<EmployeeValeDto | null | 'new'>(null);
  const [pendingCancel, setPendingCancel] = useState<EmployeeValeListItem | null>(null);
  const [verParcelasDe, setVerParcelasDe] = useState<EmployeeValeDto | null>(null);

  const hooks = useEmployeeValesHooks(employeeId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: EmployeeValeListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function openParcelas(row: EmployeeValeListItem): Promise<void> {
    try {
      setVerParcelasDe(await hooks.api.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmCancel(): Promise<void> {
    if (!pendingCancel) return;
    try {
      await deleteMut.mutateAsync(pendingCancel.id);
      toast.success(t('features.colaboradores.vales.toasts.canceled'));
      setPendingCancel(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeValeListItem>[] = [
    {
      field: 'dataSolicitacao',
      headerName: t('features.colaboradores.vales.columns.dataSolicitacao'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'valor',
      headerName: t('features.colaboradores.vales.columns.valor'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => formatBRL(value),
    },
    {
      field: 'nroParcelas',
      headerName: t('features.colaboradores.vales.columns.nroParcelas'),
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'vencimento1aParcela',
      headerName: t('features.colaboradores.vales.columns.vencimento1aParcela'),
      width: 150,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'status',
      headerName: t('features.colaboradores.vales.columns.status'),
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`features.colaboradores.vales.status.${params.row.status}`)}
          color={statusChipColor(params.row.status)}
          variant={params.row.status === 'ABE' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const editable = params.row.status === 'ABE';
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('features.colaboradores.vales.actions.parcelasTooltip')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('features.colaboradores.vales.actions.parcelas', {
                    id: params.row.id,
                  })}
                  onClick={() => void openParcelas(params.row)}
                >
                  <ReceiptIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                editable
                  ? t('common.edit')
                  : t('features.colaboradores.vales.actions.editDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!editable}
                  aria-label={t('features.colaboradores.vales.actions.edit', { id: params.row.id })}
                  onClick={() => void openEdit(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                editable
                  ? t('features.colaboradores.vales.actions.cancelTooltip')
                  : t('features.colaboradores.vales.actions.cancelDisabledTooltip')
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!editable}
                  aria-label={t('features.colaboradores.vales.actions.cancel', {
                    id: params.row.id,
                  })}
                  onClick={() => setPendingCancel(params.row)}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.colaboradores.vales.actions.new')}
        </Button>
      </Stack>

      <DataTable<EmployeeValeListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.vales.searchPlaceholder')}
      />

      <EmployeeValeFormDialog
        open={editing !== null}
        vale={editing === 'new' ? null : editing}
        hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <EmployeeValeParcelasDialog
        open={verParcelasDe !== null}
        vale={verParcelasDe}
        hooks={hooks}
        onClose={() => setVerParcelasDe(null)}
      />

      <ConfirmDialog
        open={pendingCancel !== null}
        title={t('features.colaboradores.vales.cancelConfirm.title')}
        description={t('features.colaboradores.vales.cancelConfirm.description', {
          id: pendingCancel?.id ?? '',
        })}
        confirmLabel={t('features.colaboradores.vales.actions.confirmCancel')}
        loading={deleteMut.isPending}
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(null)}
      />
    </Stack>
  );
}
